import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ArrowLeft, Calendar } from 'lucide-react';
import PublicLayout from '../../components/public/PublicLayout';
import SEO from '../../components/SEO';
import { PUBLIC_BASE } from './config';

const FALLBACK_IMG =
  'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/images/none_image.jpg';

// 인라인 color 스타일 제거 (지니 원문 잔여 스타일 정리)
function transformContent(html) {
  if (!html) return '';
  let result = html;
  result = result.replace(/\s*color\s*:\s*(?:#[0-9a-fA-F]{3,6}|rgb\([^)]+\))\s*;?/g, '');
  result = result.replace(/\s*style\s*=\s*"[\s;]*"/g, '');
  return result;
}

function PublicHotPlaceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`/api/genie-magazine/${id}`)
      .then((res) => setArticle(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center h-[60vh] gap-3 text-slate-400">
          <Loader2 size={22} className="animate-spin" />
          <span>로딩 중...</span>
        </div>
      </PublicLayout>
    );
  }

  if (!article) {
    return (
      <PublicLayout>
        <div className="flex flex-col items-center justify-center h-[60vh] gap-4 text-slate-500">
          <p>기사를 찾을 수 없습니다.</p>
          <button
            onClick={() => navigate(`${PUBLIC_BASE}/magazine/hot-place`)}
            className="text-sky-400 hover:text-sky-300 text-sm">
            목록으로 돌아가기
          </button>
        </div>
      </PublicLayout>
    );
  }

  const dateStr = article.createdAt
    ? new Date(article.createdAt).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' })
    : '';

  return (
    <PublicLayout>
      <SEO
        title={article.title || '핫플힙플'}
        description={article.title}
        path={`/magazine/hot-place/${id}`}
        image={article.thumbnail}
        type="article"
      />
      {/* 히어로 커버 */}
      {article.thumbnail && (
        <div className="relative w-full" style={{ height: 'clamp(200px, 35vw, 360px)' }}>
          <img
            src={article.thumbnail || FALLBACK_IMG}
            alt={article.title}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d1526] via-[#0d1526]/60 to-transparent" />

          <button
            onClick={() => navigate(`${PUBLIC_BASE}/magazine/hot-place`)}
            className="absolute top-4 left-4 sm:top-6 sm:left-6 z-10 flex items-center gap-1.5 px-3 py-1.5 bg-black/30 hover:bg-black/50 backdrop-blur-sm rounded-full text-white text-sm transition-colors">
            <ArrowLeft size={14} />
            목록
          </button>
        </div>
      )}

      {/* 본문 */}
      <div className={`max-w-3xl mx-auto px-4 sm:px-6 ${article.thumbnail ? '-mt-16' : 'mt-8'} relative z-10 pb-16`}>
        {!article.thumbnail && (
          <button
            onClick={() => navigate(`${PUBLIC_BASE}/magazine/hot-place`)}
            className="flex items-center gap-1.5 mb-6 text-slate-400 hover:text-white text-sm transition-colors">
            <ArrowLeft size={14} />
            목록으로 돌아가기
          </button>
        )}

        <div className="mb-6">
          <div className="flex items-center gap-2 mb-4">
            <span className="inline-block text-[11px] font-medium px-2.5 py-1 rounded-full bg-rose-500/15 text-rose-400">
              핫플힙플
            </span>
            {article.episodeNo && (
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-white/[0.06] text-slate-400">No.{article.episodeNo}</span>
            )}
          </div>
          <h1 className="text-white text-2xl sm:text-3xl font-bold leading-tight mb-3">
            {article.title}
          </h1>
          {dateStr && (
            <div className="flex items-center gap-1.5 text-slate-500 text-xs">
              <Calendar size={12} />
              <span>{dateStr}</span>
            </div>
          )}
        </div>

        {/* HTML 콘텐츠 */}
        <article
          className="music-trend-content"
          dangerouslySetInnerHTML={{ __html: transformContent(article.content) }}
        />
      </div>
    </PublicLayout>
  );
}

export default PublicHotPlaceDetailPage;
